import { useMemo, useState, useEffect } from 'react';
import { SmsEditor, SmsMessageState } from 'peak-editor-components';
import { useEditorConfig } from '../../../contexts/EditorContext';

interface SmsTemplateTabProps {
  data: any;
  onChange: (key: string, value: any) => void;
}

const COMMON_PROPS = {
    variables: { user: { name: 'John Doe', phone: '+15550123' } },
    variablesJson: JSON.stringify({ user: { name: 'John Doe', phone: '+15550123' } }, null, 2),
    onVariablesChange: () => {},
    variableError: null,
    functions: [],
    onFunctionsChange: () => {}
};

export const SmsTab: React.FC<SmsTemplateTabProps> = ({ data, onChange }) => {
  const { services } = useEditorConfig();
  const [servers, setServers] = useState<any[]>([]);

  useEffect(() => {
      let cancelled = false;
      services.notifications?.listSmsServers().then(items => {
        if (!cancelled) setServers(items);
      }).catch(error => {
        console.error('Failed to load SMS servers for workflow editor.', error);
        if (!cancelled) setServers([]);
      });
      return () => { cancelled = true; };
  }, [services.notifications]);

  const connections = useMemo(() => servers.map(s => ({
      id: s.key,
      name: `${s.key} (${s.type})`,
      dialect: 'postgres' as const, // Placeholder dialect
      connectionString: s.key
  })), [servers]);

  // Construct editor state directly from props (Controlled Component pattern)
  const editorContent: SmsMessageState = useMemo(() => {
      const defaultConnection = connections[0]?.id || '';

      return {
        body: data.message || '',
        meta: {
            connectionId: data.connectionId || defaultConnection,
            to: data.to || '',
            from: data.from || ''
        }
      };
  }, [data.message, data.connectionId, data.to, data.from, connections]);

  const handleEditorChange = useMemo(() => (newContent: SmsMessageState) => {
    // Propagate changes directly to the parent
    onChange('message', newContent.body);

    // Meta fields
    onChange('to', newContent.meta.to);
    onChange('from', newContent.meta.from);
    onChange('connectionId', newContent.meta.connectionId);
  }, [onChange]);

  return (
    <div className="flex flex-col h-full bg-slate-50">
      <div className="flex-1 relative min-h-[500px]">
        <SmsEditor
            content={editorContent}
            onChange={handleEditorChange}
            connections={connections}
            {...COMMON_PROPS}
        />
      </div>
    </div>
  );
};
